/**
 * ContactCard - Compact contact display for the Client tab
 *
 * Shows contact name, role badges, and contact info with action links.
 * Includes an edit button to open the contact editor form.
 */

import { Mail, Phone, Pencil } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useAircraftCardStore } from '@/stores/aircraftCardStore'
import type { ContactInfo } from '@/types/aircraft-card'

interface ContactCardProps {
  contact: ContactInfo
}

export function ContactCard({ contact }: ContactCardProps) {
  const openEditForm = useAircraftCardStore((s) => s.openEditForm)

  const fullName = `${contact.firstName} ${contact.lastName}`.trim()

  // Determine role badges to show
  const roles: { label: string; variant: 'default' | 'secondary' | 'outline' }[] = []
  if (contact.isPrimaryContact) {
    roles.push({ label: 'Primary', variant: 'default' })
  }
  if (contact.isBillingContact) {
    roles.push({ label: 'Billing', variant: 'secondary' })
  }
  if (contact.isOperationsContact) {
    roles.push({ label: 'Ops', variant: 'outline' })
  }

  return (
    <div className="py-1.5 border-b border-border/50 last:border-0">
      {/* Name and edit button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium">{fullName}</span>
          {contact.jobTitle && (
            <span className="text-[10px] text-muted-foreground">
              ({contact.jobTitle})
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-4 w-4 p-0 hover:bg-muted"
          onClick={() => openEditForm('contact-editor', undefined)}
          title="Edit contact"
        >
          <Pencil className="w-2.5 h-2.5 text-muted-foreground" />
        </Button>
      </div>

      {/* Role badges */}
      {roles.length > 0 && (
        <div className="flex items-center gap-1 mt-0.5">
          {roles.map((role) => (
            <Badge
              key={role.label}
              variant={role.variant}
              className="text-[9px] px-1 py-0 h-4"
            >
              {role.label}
            </Badge>
          ))}
        </div>
      )}

      {/* Contact info */}
      <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
        {contact.emailPrimary && (
          <a
            href={`mailto:${contact.emailPrimary}`}
            className="flex items-center gap-0.5 hover:text-foreground transition-colors"
            title={contact.emailPrimary}
          >
            <Mail className="w-3 h-3" />
            <span className="max-w-[80px] truncate">{contact.emailPrimary}</span>
          </a>
        )}
        {contact.phoneMobile && (
          <a
            href={`tel:${contact.phoneMobile}`}
            className="flex items-center gap-0.5 hover:text-foreground transition-colors"
            title={contact.phoneMobile}
          >
            <Phone className="w-3 h-3" />
            <span>{contact.phoneMobile}</span>
          </a>
        )}
      </div>
    </div>
  )
}
